import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import cx from "classnames";
import { Hooks, Util, Request } from "@xt/xtbase";
import store from "store";
import XtSvg from "@/components/Xt/Svg";
import { Modal, message, Checkbox, Tooltip } from "antd";
import DepositOrConvertDrawer from "../depositOrConvertDrawer";
import { post_cfd_openPosition, post_cfd_calcQuantity, post_cfd_calcMargin } from "api/v4/cfd";
import { build_prop_open_position_body, post_prop_open_position } from "api/v4/proptrading";
import Storage from "utils/storage";
import useCoinMemo from "@/components/Pages/Trade/_hook/useCoinMemo";
import useCfdConfig from "../../_hook/useCfdConfig";
import { isFiatMarketCode } from "@/utils/fiatMarket";
import LeverageSelector from "../leverageSelector";
import styles from "./index.module.scss";
import addIcon from "@/assets/img/add.png";

const { useTranslation } = Hooks;
const { getUrl, Big } = Util;

interface Props {
  onOpenPositionSuccess?: () => void;
  /** Prop 交易页：走 Prop 开仓入口（与 CFD 表单字段一致，symbol 由接口侧转换） */
  usePropOpenApi?: boolean;
  /** 仅展示：可用余额行币种后缀；不传则用交易对计价币 */
  availableBalanceDisplayUnit?: string;
}

const TradeForm: React.FC<Props> = ({
  onOpenPositionSuccess,
  usePropOpenApi = false,
  availableBalanceDisplayUnit,
}) => {
  const t = useTranslation();
  // 保证金默认填快捷面板最小一档（20）
  const [margin, setMargin] = useState("20");
  const [choiceSheetOpen, setChoiceSheetOpen] = useState(false);
  const [insufficientBalanceModalOpen, setInsufficientBalanceModalOpen] = useState(false);
  const [leverage, setLeverage] = useState("");
  const [tradeSide, setTradeSide] = useState<"buy" | "sell">("buy");
  const [loading, setLoading] = useState(false);
  // 按保证金计算数量（从 calc 接口获取，含手续费反算，更准确）
  const [calcQuantity, setCalcQuantity] = useState<number | null>(null);
  const calcQuantityRef = useRef<number | null>(null);
  calcQuantityRef.current = calcQuantity;
  const [calcQuantityLoading, setCalcQuantityLoading] = useState(false);
  // 数量快捷累加目标（BTC/ETH）；非空时不走 calc-quantity，由 effect 用 calc-margin 按总量反算保证金
  const [shortcutQuantityTotal, setShortcutQuantityTotal] = useState<number | null>(null);
  const shortcutQuantityTotalRef = useRef<number | null>(null);
  shortcutQuantityTotalRef.current = shortcutQuantityTotal;
  const shortcutMarginReqId = useRef(0);
  /** 使进行中的 calc-quantity 回包失效，避免晚到的结果覆盖数量快捷累加后的展示 */
  const calcQuantityReqId = useRef(0);
  /** 与 tradeFormPro 一致：calc-quantity 防抖，避免 margin 连续变化时疯狂请求 */
  const calcQuantityDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** 拖动保证金滑块过程中不发 calc-quantity，松手后再走下方 effect */
  const [isMarginSliderDragging, setIsMarginSliderDragging] = useState(false);
  // 保证金加减号步长：默认 5；点击快捷面板后改为最近一次点击的档位金额
  const [marginAdjustStep, setMarginAdjustStep] = useState(5);
  // 数量加减号步长：默认 0.001；点击快捷面板后改为最近一次点击的档位数量
  const [quantityAdjustStep, setQuantityAdjustStep] = useState(0.001);
  const [quantityInputValue, setQuantityInputValue] = useState("");

  // 获取K线颜色方案
  const [klineColorScheme, setKlineColorScheme] = useState<"green_up" | "red_up">(() => {
    const savedScheme = Storage.get("klineColorScheme") as "green_up" | "red_up";
    return savedScheme === "red_up" || savedScheme === "green_up" ? savedScheme : "green_up";
  });

  // 监听K线颜色方案变化
  useEffect(() => {
    const handleColorSchemeChange = () => {
      const savedScheme = Storage.get("klineColorScheme") as "green_up" | "red_up";
      const currentScheme = savedScheme === "red_up" || savedScheme === "green_up" ? savedScheme : "green_up";
      setKlineColorScheme(currentScheme);
    };
    
    window.addEventListener("klineColorSchemeChange", handleColorSchemeChange);
    return () => {
      window.removeEventListener("klineColorSchemeChange", handleColorSchemeChange);
    };
  }, []);

  const { name } = store.market;

  // 当交易对变化时，重置表单内容（保证金重置为快捷面板最小档）
  useEffect(() => {
    setMargin("20");
    setLeverage("");
    setTradeSide("buy");
    setShortcutQuantityTotal(null);
    setCalcQuantity(null);
    setMarginAdjustStep(5);
    setQuantityAdjustStep(0.001);
    setQuantityInputValue("");
  }, [name]);
  const { isLogin, authInfo } = store.user;
  const { 
    coinQuantityUpperCase, 
    coinPriceUpperCase, 
    coinQuantityPrecisionMarket, 
    coinPricePrecisionMarket, 
    coinQuantityPrecisionCurrency,
    coinPricePrecisionCurrency
  } = useCoinMemo();
  const cfdConfig = useCfdConfig();

  // 获取完整的可用余额（不取整）；Prop 页由 useUserLogin 将 USDX 资产写入 currencyPrice
  const availableBalanceLabel = useMemo(() => {
    if (!store.balances?.currencyPrice?.availableAmount) return "0";
    // 和现货保持一致，使用 toFixedMaxCy 格式化
    const precision = coinPricePrecisionCurrency && coinPricePrecisionCurrency > 0 ? coinPricePrecisionCurrency : 8;
    return Big(store.balances.currencyPrice.availableAmount).toFixedMaxCy(precision);
  }, [store.balances?.currencyPrice?.availableAmount, coinPricePrecisionCurrency]);
  const availableBalanceNum = useMemo(
    () => Big(store.balances?.currencyPrice?.availableAmount || 0),
    [store.balances?.currencyPrice?.availableAmount]
  );

  // 货币简写映射
  const currencyAbbreviationMap: Record<string, string> = {
    USDT: "U",
    AED: "AED",
    USDX: "USDX",
    // 其他货币如果没有简写，使用完整名称
  };

  // 获取货币显示名称（优先使用简写）
  const currencyDisplayName = useMemo(() => {
    return currencyAbbreviationMap[coinPriceUpperCase] || coinPriceUpperCase;
  }, [coinPriceUpperCase]);

  // 快速选择保证金 - 根据报价货币动态生成
  const quickMarginOptions = useMemo(() => {
    const amounts = ["20", "50", "100", "200"];
    return amounts.map((amount) => `${amount}${currencyDisplayName}`);
  }, [currencyDisplayName]);

  // 当前交易对基础币种（小写），用于仅对 BTC/ETH 展示数量快捷选择
  const baseCoin = useMemo(() => {
    const base = name ? name.split("_")[0] : "";
    return typeof base === "string" ? base.toLowerCase() : "";
  }, [name]);

  // 数量快捷选项：仅 BTC(0.1,0.2,0.3,0.5) 与 ETH(1,2,3,5)
  const quickQuantityOptions = useMemo(() => {
    if (baseCoin === "btc") return [0.1, 0.2, 0.3, 0.5];
    if (baseCoin === "eth") return [1, 2, 3, 5];
    return [];
  }, [baseCoin]);
  // 基础版暂时隐藏 BTC/ETH 的 quantity 输入和快捷面板，后续需要时再恢复。
  const showQuantityControl = false;

  const quantityPrecisionForShortcut = useMemo(() => {
    return coinQuantityPrecisionCurrency && coinQuantityPrecisionCurrency > 0
      ? coinQuantityPrecisionCurrency
      : coinQuantityPrecisionMarket && coinQuantityPrecisionMarket > 0
        ? coinQuantityPrecisionMarket
        : 8;
  }, [coinQuantityPrecisionCurrency, coinQuantityPrecisionMarket]);

  const formatQuantityValue = useCallback((value: number | string) => {
    const fixed = Big(value || 0).toFixed(quantityPrecisionForShortcut);
    return fixed.replace(/\.?0+$/, "") || "0";
  }, [quantityPrecisionForShortcut]);

  useEffect(() => {
    if (shortcutQuantityTotal != null) return;
    if (calcQuantity != null && calcQuantity > 0 && !Number.isNaN(calcQuantity)) {
      setQuantityInputValue(formatQuantityValue(calcQuantity));
      return;
    }
    if (!margin || Big(margin || 0).lte(0)) {
      setQuantityInputValue("");
    }
  }, [shortcutQuantityTotal, calcQuantity, margin, formatQuantityValue]);

  const quantityInputDisplayValue = useMemo(() => {
    if (shortcutQuantityTotal != null) return quantityInputValue;
    if (quantityInputValue) return quantityInputValue;
    return "";
  }, [shortcutQuantityTotal, quantityInputValue]);

  // 处理保证金输入，过滤非数字
  const handleMarginInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    
    // 允许空字符串
    if (newVal === "") {
      setShortcutQuantityTotal(null);
      setCalcQuantity(null);
      setQuantityInputValue("");
      setQuantityAdjustStep(0.001);
      setMargin("");
      return;
    }
    
    // 只允许数字和小数点
    if (!/^[0-9]+\.?[0-9]*$/.test(newVal)) {
      return;
    }
    
    // 不允许以0开头（除了0.或0）
    if (/^0/.test(newVal) && !/^0(\.|$)/.test(newVal)) {
      return;
    }
    
    // 限制小数位数（使用保证金精度或价格精度）
    const marginPrecision = cfdConfig.marginPrecision !== undefined ? cfdConfig.marginPrecision : (coinPricePrecisionMarket && coinPricePrecisionMarket > 0 ? coinPricePrecisionMarket : 2);
    if (typeof marginPrecision === "number" && /\./.test(newVal)) {
      const decimalPart = newVal.split(".")[1];
      if (decimalPart && decimalPart.length > marginPrecision) {
        return;
      }
    }
    
    setShortcutQuantityTotal(null);
    // 保留当前数量展示，等 calc-quantity 返回后再覆盖，避免输入框闪空
    setQuantityAdjustStep(0.001);
    setMargin(newVal);
  }, [cfdConfig.marginPrecision, coinPricePrecisionMarket]);

  const handleQuantityInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;

    if (newVal === "") {
      calcQuantityReqId.current += 1;
      setQuantityInputValue("");
      setShortcutQuantityTotal(null);
      setCalcQuantity(null);
      setMargin("");
      return;
    }

    if (!/^[0-9]+\.?[0-9]*$/.test(newVal)) {
      return;
    }

    if (/^0/.test(newVal) && !/^0(\.|$)/.test(newVal)) {
      return;
    }

    if (typeof quantityPrecisionForShortcut === "number" && /\./.test(newVal)) {
      const decimalPart = newVal.split(".")[1];
      if (decimalPart && decimalPart.length > quantityPrecisionForShortcut) {
        return;
      }
    }

    const quantityNum = Number(newVal);
    calcQuantityReqId.current += 1;
    setQuantityInputValue(newVal);
    setShortcutQuantityTotal(quantityNum);
    setCalcQuantity(quantityNum);
    if (quantityNum <= 0) {
      setMargin("0");
    }
  }, [quantityPrecisionForShortcut]);

  // 保证金加减（步进为 marginAdjustStep，默认 5）
  const handleAdjustMargin = useCallback(
    (delta: number) => {
      const marginPrecisionVal =
        cfdConfig.marginPrecision !== undefined
          ? cfdConfig.marginPrecision
          : coinPricePrecisionMarket && coinPricePrecisionMarket > 0
            ? coinPricePrecisionMarket
            : 2;
      const current = margin && margin.trim() ? Big(margin) : Big(0);
      if (delta > 0 && availableBalanceNum.lte(0)) {
        setInsufficientBalanceModalOpen(true);
        return;
      }
      if (delta > 0 && current.gte(availableBalanceNum)) {
        setInsufficientBalanceModalOpen(true);
        return;
      }
      const next = current.plus(delta);
      let safeNext = next.lt(0) ? Big(0) : next;
      if (delta > 0 && safeNext.gte(availableBalanceNum)) {
        safeNext = availableBalanceNum;
        setInsufficientBalanceModalOpen(true);
      }
      const fixed = safeNext.toFixed(marginPrecisionVal);
      const normalized = fixed.replace(/\.?0+$/, "");
      setShortcutQuantityTotal(null);
      // 保留当前数量展示，等 calc-quantity 返回后再覆盖，避免输入框闪空
      setQuantityAdjustStep(0.001);
      setMargin(normalized || "0");
    },
    [margin, cfdConfig.marginPrecision, coinPricePrecisionMarket, availableBalanceNum]
  );

  const handleAdjustQuantity = useCallback((delta: number) => {
    calcQuantityReqId.current += 1;
    const current =
      quantityInputDisplayValue && quantityInputDisplayValue.trim()
        ? Big(quantityInputDisplayValue)
        : Big(0);
    let next = current.plus(delta);
    if (next.lt(0)) {
      next = Big(0);
    }
    const nextStr = formatQuantityValue(next.toString());
    const nextNum = Number(nextStr);
    setQuantityInputValue(nextStr);
    setShortcutQuantityTotal(nextNum);
    setCalcQuantity(nextNum);
    if (nextNum <= 0) {
      setMargin("0");
    }
  }, [quantityInputDisplayValue, formatQuantityValue]);

  // 快捷保证金：每次点击累加对应档位，不超过可用余额（不按金额高亮选中态，仅依赖按钮 :active 样式）
  const handleQuickMarginAdd = useCallback(
    (deltaStr: string) => {
      const marginPrecisionVal =
        cfdConfig.marginPrecision !== undefined
          ? cfdConfig.marginPrecision
          : coinPricePrecisionMarket && coinPricePrecisionMarket > 0
            ? coinPricePrecisionMarket
            : 2;
      const available = availableBalanceNum;
      const delta = Big(deltaStr || 0);
      if (delta.lte(0)) return;
      if (available.lte(0)) {
        setInsufficientBalanceModalOpen(true);
        return;
      }
      setMarginAdjustStep(Number(deltaStr));
      setShortcutQuantityTotal(null);
      // 保留当前数量展示，等 calc-quantity 返回后再覆盖，避免输入框闪空
      setQuantityAdjustStep(0.001);
      setMargin((prev) => {
        const current = prev && prev.trim() ? Big(prev) : Big(0);
        if (current.gte(available)) {
          setInsufficientBalanceModalOpen(true);
          return prev || "0";
        }
        let next = current.plus(delta);
        if (next.gte(available)) {
          next = available;
          setInsufficientBalanceModalOpen(true);
        }
        const fixed = next.toFixed(marginPrecisionVal);
        const normalized = fixed.replace(/\.?0+$/, "");
        return normalized || "0";
      });
    },
    [cfdConfig.marginPrecision, coinPricePrecisionMarket, availableBalanceNum]
  );

  const marginSliderPercent = useMemo(() => {
    if (availableBalanceNum.lte(0)) return 0;
    const percent = Big(margin || 0).div(availableBalanceNum).times(100);
    if (percent.lte(0)) return 0;
    if (percent.gte(100)) return 100;
    return Number(percent.toFixed(0));
  }, [margin, availableBalanceNum]);

  const setMarginByPercent = useCallback(
    (percent: number) => {
      const marginPrecisionVal =
        cfdConfig.marginPrecision !== undefined
          ? cfdConfig.marginPrecision
          : coinPricePrecisionMarket && coinPricePrecisionMarket > 0
            ? coinPricePrecisionMarket
            : 2;
      if (availableBalanceNum.lte(0)) {
        if (percent > 0) setInsufficientBalanceModalOpen(true);
        setMargin("0");
        return;
      }
      const safePercent = Math.max(0, Math.min(100, percent));
      const next = availableBalanceNum.times(safePercent).div(100);
      const fixed = next.toFixed(marginPrecisionVal);
      const normalized = fixed.replace(/\.?0+$/, "");
      setShortcutQuantityTotal(null);
      setQuantityAdjustStep(0.001);
      setMargin(normalized || "0");
    },
    [availableBalanceNum, cfdConfig.marginPrecision, coinPricePrecisionMarket]
  );

  const handleMarginSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMarginByPercent(Number(e.target.value));
    },
    [setMarginByPercent]
  );

  const handleMarginSliderPointerDown = useCallback(() => {
    setIsMarginSliderDragging(true);
    const end = () => {
      setIsMarginSliderDragging(false);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
    };
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  }, []);

  // 根据配置设置默认杠杆：最小杠杆小于 50 时默认 50，否则默认最小杠杆
  useEffect(() => {
    if (cfdConfig.leverageRange) {
      const { min, max } = cfdConfig.leverageRange;
      const defaultLeverage = Math.max(min, 50);
      // 如果当前杠杆为空或不在范围内，设置为默认杠杆
      if (!leverage || Number(leverage) < min || Number(leverage) > max) {
        setLeverage(String(defaultLeverage));
      }
    } else if (!leverage) {
      // 如果没有配置，默认使用100
      setLeverage("100");
    }
  }, [cfdConfig.leverageRange, leverage]);

  // 从 store.cfdView.cfdTicker 获取当前价格（高频数据独立 store，避免整页重渲染）
  const currentPrice = useMemo(() => {
    const ticker = store.cfdView.cfdTicker;
    if (ticker && ticker.c && ticker.s === name) {
      return String(ticker.c);
    }
    return "0";
  }, [store.cfdView.cfdTicker, name]);

  // 数量快捷：每次点击累加对应数量，由下方 effect 用 calc-margin 按总量反算保证金
  const handleQuantityShortcutAdd = useCallback(
    (deltaQty: number) => {
      if (deltaQty <= 0) return;
      // 取消尚未返回的 calc-quantity，否则晚到的 setCalcQuantity 会覆盖累加数量，看起来像「只按最后一次点击」
      calcQuantityReqId.current += 1;
      setQuantityAdjustStep(deltaQty);
      setShortcutQuantityTotal((prev) => {
        // 首次点数量快捷：在「当前保证金对应的数量」上累加，不要从零覆盖原金额
        const baseQty =
          prev != null
            ? Big(prev)
            : Big(
                calcQuantityRef.current != null &&
                  calcQuantityRef.current > 0 &&
                  !Number.isNaN(calcQuantityRef.current)
                  ? calcQuantityRef.current
                  : 0
              );
        const next = baseQty.plus(deltaQty);
        const nextStr = formatQuantityValue(next.toString());
        const nextNum = Number(nextStr);
        setQuantityInputValue(nextStr);
        setCalcQuantity(nextNum);
        return nextNum;
      });
    },
    [formatQuantityValue]
  );

  // 数量快捷累加后：按总量请求 calc-margin 更新保证金与展示数量
  useEffect(() => {
    if (shortcutQuantityTotal == null) return;
    if (shortcutQuantityTotal <= 0) {
      setMargin("0");
      setCalcQuantity(0);
      setCalcQuantityLoading(false);
      return;
    }
    if (!isLogin || !name || !leverage || Number(leverage) <= 0) return;
    const priceNum = Number(currentPrice) || 0;
    if (priceNum <= 0) return;
    const marginPrecisionVal =
      cfdConfig.marginPrecision !== undefined
        ? cfdConfig.marginPrecision
        : coinPricePrecisionMarket && coinPricePrecisionMarket > 0
          ? coinPricePrecisionMarket
          : 2;
    const totalQty = shortcutQuantityTotal;
    const reqId = ++shortcutMarginReqId.current;
    setCalcQuantityLoading(true);
    post_cfd_calcMargin({
      data: {
        symbol: name,
        quantity: totalQty,
        leverage: Number(leverage),
        priceType: 2,
        price: priceNum,
        side: tradeSide === "buy" ? 1 : 2,
      },
      errorPop: false,
      autoErrorModal: false,
    })
      .then((res: any) => {
        if (reqId !== shortcutMarginReqId.current) return;
        const data = res?.result ?? res?.data ?? res;
        const total = data?.totalMargin;
        if (total != null && !Number.isNaN(Number(total)) && Number(total) > 0) {
          setMargin(Number(total).toFixed(marginPrecisionVal));
        } else {
          const fallbackMargin = (totalQty * priceNum) / Number(leverage);
          setMargin(fallbackMargin.toFixed(marginPrecisionVal));
        }
        setCalcQuantity(totalQty);
      })
      .catch(() => {
        if (reqId !== shortcutMarginReqId.current) return;
        const fallbackMargin = (totalQty * priceNum) / Number(leverage);
        setMargin(fallbackMargin.toFixed(marginPrecisionVal));
        setCalcQuantity(totalQty);
      })
      .finally(() => {
        if (reqId !== shortcutMarginReqId.current) return;
        setCalcQuantityLoading(false);
      });
  }, [
    shortcutQuantityTotal,
    isLogin,
    name,
    leverage,
    currentPrice,
    tradeSide,
    cfdConfig.marginPrecision,
    coinPricePrecisionMarket,
  ]);

  // 按保证金计算数量：仅在金额（保证金）变化时调用；若当前是数量快捷累加态则不请求，以快捷总量为准
  // 与 tradeFormPro 对齐：拖动滑块时不请求；松手后 180ms 防抖再请求，避免拖动过程频繁打 calc-quantity
  useEffect(() => {
    if (calcQuantityDebounceTimer.current) {
      clearTimeout(calcQuantityDebounceTimer.current);
      calcQuantityDebounceTimer.current = null;
    }
    if (isMarginSliderDragging) {
      return;
    }
    if (!isLogin || !name || !margin || Big(margin).lte(0) || !leverage || Number(leverage) <= 0) {
      calcQuantityReqId.current += 1; // 使在途 calc-quantity 失效，避免清空保证金后晚到回包又写入数量
      setCalcQuantity(null);
      return;
    }
    if (shortcutQuantityTotal != null) return; // 数量快捷累加时只走 calc-margin，不触发 calc-quantity
    const marginNum = Number(margin);
    const leverageNum = Number(leverage);
    const priceNum = currentPrice ? Number(currentPrice) : undefined;
    const reqId = ++calcQuantityReqId.current;
    setCalcQuantityLoading(true);
    calcQuantityDebounceTimer.current = setTimeout(() => {
      post_cfd_calcQuantity({
        data: {
          symbol: name,
          margin: marginNum,
          leverage: leverageNum,
          priceType: 2, // 市价
          price: priceNum,
          side: tradeSide === "buy" ? 1 : 2,
        },
        errorPop: false,
        autoErrorModal: false,
      })
        .then((res: any) => {
          if (reqId !== calcQuantityReqId.current) return;
          if (shortcutQuantityTotalRef.current != null) return;
          const data = res?.result ?? res?.data ?? res;
          const q = data?.quantity;
          if (q != null && !Number.isNaN(Number(q))) {
            setCalcQuantity(Number(q));
          } else {
            setCalcQuantity(null);
          }
        })
        .catch(() => {
          if (reqId !== calcQuantityReqId.current) return;
          if (shortcutQuantityTotalRef.current != null) return;
          setCalcQuantity(null);
        })
        .finally(() => {
          setCalcQuantityLoading(false);
        });
    }, 180);
    return () => {
      if (calcQuantityDebounceTimer.current) {
        clearTimeout(calcQuantityDebounceTimer.current);
        calcQuantityDebounceTimer.current = null;
      }
    };
  }, [margin, name, leverage, isLogin, tradeSide, shortcutQuantityTotal, isMarginSliderDragging]);

  // 开仓价值仍为 保证金*杠杆；数量改为使用 calc 接口返回值，不再前端计算
  const quantityPrecision = coinQuantityPrecisionCurrency && coinQuantityPrecisionCurrency > 0
    ? coinQuantityPrecisionCurrency
    : (coinQuantityPrecisionMarket && coinQuantityPrecisionMarket > 0 ? coinQuantityPrecisionMarket : 8);
  const marginPrecision = cfdConfig.marginPrecision !== undefined ? cfdConfig.marginPrecision : (coinPricePrecisionMarket && coinPricePrecisionMarket > 0 ? coinPricePrecisionMarket : 2);
  const { quantity: quantityDisplay, value } = useMemo(() => {
    const marginNum = Big(margin || 0);
    const leverageNum = Big(leverage || 1);
    const displayQuantity = shortcutQuantityTotal != null ? shortcutQuantityTotal : calcQuantity;
    // 开仓价值 = 保证金 * 杠杆（展示用）
    const calculatedValue = marginNum.times(leverageNum);
    const quantityStr =
      displayQuantity != null && displayQuantity > 0
        ? Big(displayQuantity).toFixedCy(quantityPrecision)
        : displayQuantity === 0
          ? "0"
        : calcQuantityLoading
          ? "..."
          : "--";
    return {
      quantity: quantityStr,
      value: calculatedValue.gt(0) ? calculatedValue.toFixedCy(marginPrecision) : "--",
    };
  }, [margin, leverage, shortcutQuantityTotal, calcQuantity, calcQuantityLoading, quantityPrecision, marginPrecision]);

  // 止盈止损范围显示文本
  const tpSlRangeText = useMemo(() => {
    if (cfdConfig.tpSlRange) {
      const { max, min } = cfdConfig.tpSlRange;
      return `+${max * 100}%/-${min * 100}%`;
    }
    return "+XXX/-XXX";
  }, [cfdConfig.tpSlRange]);

  const activeSideBtnClass = useMemo(() => {
    if (tradeSide === "buy") {
      return klineColorScheme === "green_up" ? styles.buyBtn : styles.sellBtn;
    }
    return klineColorScheme === "green_up" ? styles.sellBtn : styles.buyBtn;
  }, [tradeSide, klineColorScheme]);
  const buySideColorClass = klineColorScheme === "green_up" ? styles.positiveSide : styles.negativeSide;
  const sellSideColorClass = klineColorScheme === "green_up" ? styles.negativeSide : styles.positiveSide;

  // 提交开仓订单
  const handleSubmit = useCallback((side: "buy" | "sell") => {
    if (loading) return;
    if (!isLogin) {
      const query = "?backurl=" + encodeURIComponent(location.href);
      location.href = getUrl("/accounts/login" + query);
      return;
    }

    // 验证保证金
    const marginNum = Big(margin || 0);
    if (marginNum.lte(0)) {
      setInsufficientBalanceModalOpen(true);
      return;
    }

    // 验证可用余额
    if (availableBalanceNum.lt(marginNum)) {
      setInsufficientBalanceModalOpen(true);
      return;
    }

    // 优先使用 calc 接口返回的数量；若无则回退到前端计算（避免 calc 失败时无法下单）
    const leverageNum = Big(leverage || 1);
    const priceNum = Big(currentPrice || 0);
    const fallbackQuantity = priceNum.gt(0) ? marginNum.times(leverageNum).div(priceNum) : Big(0);
    const useCalcQuantity = calcQuantity != null && calcQuantity > 0;
    const effectiveQuantity = useCalcQuantity ? Big(calcQuantity) : fallbackQuantity;

    console.log("[CFD 开仓校验]", {
      currentPrice,
      priceNum: priceNum.toString(),
      margin: marginNum.toString(),
      leverage: leverageNum.toString(),
      calcQuantity,
      useCalcQuantity,
      fallbackQuantity: fallbackQuantity.toString(),
      effectiveQuantity: effectiveQuantity.toString(),
      effectiveQuantityLte0: effectiveQuantity.lte(0),
    });

    if (effectiveQuantity.lte(0)) {
      console.warn("[CFD 开仓拦截] Invalid open quantity: effectiveQuantity <= 0", {
        currentPrice,
        calcQuantity,
        fallbackQuantity: fallbackQuantity.toString(),
      });
      message.error(t("cfd.invalidOpenQuantity"));
      return;
    }

    // 验证开仓数量是否小于精度（四舍五入后为0）
    const quantityPrecisionForValidate = coinQuantityPrecisionCurrency && coinQuantityPrecisionCurrency > 0
      ? coinQuantityPrecisionCurrency
      : (coinQuantityPrecisionMarket && coinQuantityPrecisionMarket > 0 ? coinQuantityPrecisionMarket : 8);

    const roundedQuantity = Number(effectiveQuantity.toFixed(quantityPrecisionForValidate));
    if (roundedQuantity <= 0) {
      console.warn("[CFD 开仓拦截] quantityTooSmall: 按精度四舍五入后 <= 0", {
        effectiveQuantity: effectiveQuantity.toString(),
        quantityPrecisionForValidate,
        roundedQuantity,
      });
      message.error(t("cfd.quantityTooSmall"));
      return;
    }

    // 检查是否需要二次确认（市价单，默认开启）
    const orderConfirm_market = Storage.get("orderConfirm_market");
    // 如果 Storage 中没有值，默认为 true（开启）
    const needConfirm = orderConfirm_market === undefined || orderConfirm_market === null ? true : orderConfirm_market !== false;

    // 股票/大宗(plateId 260/261)：若最新价格已超过半小时未更新，开仓前二次确认（时间取 socket ticker 与 kline 接口最新一根中较新者，进页后 socket 未推送过时用 kline 时间）
    const plates = store.market.currentConfig?.plates;
    const isStockOrCommodity = Array.isArray(plates) && (plates.includes(260) || plates.includes(261));
    const HALF_HOUR_MS = 30 * 60 * 1000;
    const tickerTime = store.cfdView.cfdTickerUpdateTime || 0;
    const klineTime = store.cfdView.lastKlineBarTime || 0;
    const effectivePriceUpdateTime = Math.max(tickerTime, klineTime);
    const isPriceStale = effectivePriceUpdateTime === 0 || Date.now() - effectivePriceUpdateTime > HALF_HOUR_MS;
    const needNonTradingHoursConfirm = isStockOrCommodity && isPriceStale;

    // 提交订单的函数：同时传 margin 与 quantity，接口优先以保证金(margin)作为下单基准，避免前端计算 quantity 误差导致下单失败
    const submitOrder = () => {
      setLoading(true);
      const marginPrecision = cfdConfig.marginPrecision !== undefined ? cfdConfig.marginPrecision : (coinPricePrecisionMarket && coinPricePrecisionMarket > 0 ? coinPricePrecisionMarket : 2);
      const orderData = {
        symbol: name,
        side: side === "buy" ? 1 : 2, // 1=买(LONG), 2=卖(SHORT)
        priceType: 2, // 2=市价
        quantity: Number(effectiveQuantity.toFixed(coinQuantityPrecisionMarket || 8)), // 优先用 calc 接口数量，否则前端回退
        margin: Number(marginNum.toFixed(marginPrecision)), // 保证金；与 quantity 二选一，同时传时接口优先按 margin
        leverage: Number(leverage),
        keep: 0, // 0=关闭持仓过夜，1=开启
      };

      const openReq = usePropOpenApi
        ? post_prop_open_position({
            data: build_prop_open_position_body(
              name,
              {
                side: orderData.side,
                quantity: orderData.quantity,
                priceType: orderData.priceType,
                leverage: orderData.leverage,
                keep: orderData.keep,
              },
              { propTradingPage: usePropOpenApi },
            ),
            errorPop: true,
            successPop: () => message.success(t("cfd.placeOrderSuccess")),
          })
        : post_cfd_openPosition({
            data: orderData,
            errorPop: true,
            successPop: () => message.success(t("cfd.placeOrderSuccess")),
          });

      openReq
        .then((data) => {
          console.log("【CFD 开仓成功】", data);
          // 清空表单后，保证金恢复为默认（快捷面板最小档）
          setMargin("20");
          setShortcutQuantityTotal(null);
          setCalcQuantity(null);
          setQuantityAdjustStep(0.001);
          setQuantityInputValue("");
          // 刷新持仓列表和订单列表
          onOpenPositionSuccess && onOpenPositionSuccess();
        })
        .catch((err) => {
          console.error("【CFD 开仓失败】", err);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    // 继续到订单确认或直接提交
    const proceedToOrderConfirmOrSubmit = () => {
      if (needConfirm) {
        let disableOrderConfirmQuickly = false;
        Modal.confirm({
          className: styles.orderConfirmModal,
          title: t("trade.orderConfirm"),
          width: 400,
          centered: true,
          closable: true,
          closeIcon: <XtSvg icon="close" />,
          icon: null,
          okText: t("confirm"),
          cancelText: t("cancel"),
          onOk: () => {
            if (disableOrderConfirmQuickly) {
              store.trade.updateState({
                orderConfirm_limit: false,
                orderConfirm_market: false,
              });
              Storage.set("orderConfirm_limit", false);
              Storage.set("orderConfirm_market", false);
            }
            return submitOrder();
          },
          content: (
            <div className={styles.orderConfirmContent}>
              <div className={styles.orderConfirmRow}>
                <div className={styles.orderConfirmLabel}>{t("cfd.direction")}</div>
                <div className={cx(styles.orderConfirmValue, side === "buy" ? styles.buy : styles.sell)}>
                  {side === "buy" ? t("cfd.buyUp") : t("cfd.buyDown")}
                </div>
              </div>
              <div className={styles.orderConfirmRow}>
                <div className={styles.orderConfirmLabel}>{t("cfd.margin")}</div>
                <div className={styles.orderConfirmValue}>{margin} {coinPriceUpperCase}</div>
              </div>
              <div className={styles.orderConfirmRow}>
                <div className={styles.orderConfirmLabel}>{t("cfd.leverage")}</div>
                <div className={styles.orderConfirmValue}>{leverage}X</div>
              </div>
              <div className={styles.orderConfirmRow}>
                <div className={styles.orderConfirmLabel}>{t("cfd.openQuantity")}</div>
                <div className={styles.orderConfirmValue}>
                  {effectiveQuantity.toFixed(coinQuantityPrecisionMarket || 8)} {coinQuantityUpperCase}
                </div>
              </div>
              <div className={styles.orderConfirmRow}>
                <div className={styles.orderConfirmLabel}>{t("cfd.openValue")}</div>
                <div className={styles.orderConfirmValue}>
                  {marginNum.times(leverageNum).toFixed(cfdConfig.marginPrecision !== undefined ? cfdConfig.marginPrecision : (coinPricePrecisionMarket || 2))} {coinPriceUpperCase}
                </div>
              </div>
              <div className={styles.quickConfirmToggle}>
                <Checkbox onChange={(e) => { disableOrderConfirmQuickly = !!e.target.checked; }}>
                  {t("cfd.skipOrderConfirmNext")}
                </Checkbox>
                <Tooltip title={t("cfd.reEnableOrderConfirmTip")} placement="top" overlayClassName={styles.quickConfirmTooltip}>
                  <span className={styles.quickConfirmInfoIcon}>i</span>
                </Tooltip>
              </div>
            </div>
          ),
        });
      } else {
        submitOrder();
      }
    };

    // 非交易时段（股票/大宗且价格超 30 分钟未更新）：先弹二次确认再走订单确认或提交
    if (needNonTradingHoursConfirm) {
      Modal.confirm({
        width: 400,
        centered: true,
        closable: true,
        closeIcon: <XtSvg icon="close" />,
        icon: null,
        okText: t("confirm"),
        cancelText: t("cancel"),
        content: t("cfd.nonTradingHoursOpenConfirm"),
        onOk: proceedToOrderConfirmOrSubmit,
      });
    } else {
      proceedToOrderConfirmOrSubmit();
    }
  }, [loading, isLogin, margin, leverage, currentPrice, name, calcQuantity, coinQuantityPrecisionCurrency, coinQuantityPrecisionMarket, coinPricePrecisionMarket, coinQuantityUpperCase, coinPriceUpperCase, cfdConfig.marginPrecision, t, availableBalanceNum]);

  // 打开底部弹窗：未登录跳转登录页，已登录选择「充值法币」或「闪兑」
  const handleAddClick = useCallback(() => {
    if (!isLogin) {
      location.href = getUrl("/accounts/login?backurl=" + encodeURIComponent(typeof location !== "undefined" ? location.href : ""));
      return;
    }
    setChoiceSheetOpen(true);
  }, [isLogin]);

  const isFiatContract = useMemo(() => {
    const quoteCurrency = name ? name.split("_")[1]?.toUpperCase() : "USDT";
    return isFiatMarketCode(quoteCurrency);
  }, [name]);

  const handleChooseDeposit = useCallback(() => {
    if (isFiatContract) {
      location.href = getUrl("/wallet/account/common/deposit/fiat");
    } else {
      location.href = getUrl("/wallet/account/common/deposit?currency=usdt");
    }
  }, [isFiatContract]);

  // 余额不足弹窗：点击充值跳转到对应法币/加密货币充值页
  const handleInsufficientBalanceDeposit = useCallback(() => {
    setInsufficientBalanceModalOpen(false);
    const quoteCurrency = name ? name.split("_")[1]?.toUpperCase() : "USDT";
    const isFiat = isFiatMarketCode(quoteCurrency);
    if (isFiat) {
      location.href = getUrl("/wallet/account/common/deposit/fiat");
    } else {
      location.href = getUrl("/wallet/account/common/deposit?currency=usdt");
    }
  }, [name]);

  return (
    <div className={styles.tradeForm}>
      <div className={styles.sideTabs}>
        <button
          type="button"
          className={cx(styles.sideTab, buySideColorClass, { [styles.active]: tradeSide === "buy" })}
          onClick={() => setTradeSide("buy")}
        >
          {t("cfd.buyUp")}
        </button>
        <button
          type="button"
          className={cx(styles.sideTab, sellSideColorClass, { [styles.active]: tradeSide === "sell" })}
          onClick={() => setTradeSide("sell")}
        >
          {t("cfd.buyDown")}
        </button>
      </div>

      <div className={styles.marginSection}>
        {/* 仅保证金输入区作为新手引导锚点，不含开仓数量，避免高亮框与真实「保证金」区域错位 */}
        <div className={styles.marginGuideAnchor} data-guide-target="marginSection">
        <div className={styles.marginHeader}>
          <span className={styles.label}>{t("cfd.margin")}</span>
          <div className={styles.available}>
            <span>
              {t("cfd.available")}: {availableBalanceLabel}{" "}
              {availableBalanceDisplayUnit ?? coinPriceUpperCase}
            </span>
            <button type="button" className={styles.addBtn} data-guide-target="cfdStandardGuideDeposit" onClick={handleAddClick}>
              <img src={addIcon} alt="add" />
            </button>
          </div>
        </div>
        <div className={styles.marginInput}>
          <span
            className={styles.marginAdjustBtn}
            role="button"
            tabIndex={0}
            onClick={() => handleAdjustMargin(-marginAdjustStep)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleAdjustMargin(-marginAdjustStep)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="none">
              <path d="M4 8H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <input type="text" placeholder={t("cfd.pleaseEnter")} value={margin} onChange={handleMarginInput} />
          <span
            className={styles.marginAdjustBtn}
            role="button"
            tabIndex={0}
            onClick={() => handleAdjustMargin(marginAdjustStep)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleAdjustMargin(marginAdjustStep)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="none">
              <path d="M8 4V12M4 8H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
        <div className={styles.quickOptions}>
          {quickMarginOptions.map((option) => {
            const optionValue = option.replace(currencyDisplayName, "");
            return (
              <button
                key={option}
                type="button"
                className={styles.quickBtn}
                onClick={() => handleQuickMarginAdd(optionValue)}
              >
                {option}
              </button>
            );
          })}
        </div>
        <div
          className={styles.marginSlider}
          style={{ "--progress": `${marginSliderPercent}%` } as React.CSSProperties}
        >
          <div className={styles.marginSliderTooltip} style={{ left: `${marginSliderPercent}%` }}>
            <div className={styles.marginSliderTooltipContent}>{marginSliderPercent}%</div>
            <div className={styles.marginSliderTooltipArrow} />
          </div>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={marginSliderPercent}
            onChange={handleMarginSliderChange}
            onPointerDown={(e) => {
              e.stopPropagation();
              handleMarginSliderPointerDown();
            }}
            onPointerMove={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            aria-label={t("cfd.margin")}
          />
          <div className={styles.marginSliderMarks} aria-hidden="true">
            {[0, 25, 50, 75, 100].map((percent) => (
              <span
                key={percent}
                className={cx(styles.marginSliderMark, { [styles.marginSliderMarkActive]: marginSliderPercent >= percent })}
              />
            ))}
          </div>
        </div>
        </div>
        {showQuantityControl && quickQuantityOptions.length > 0 && (
          <div className={styles.quantityControlSection}>
            <span className={styles.label}>{t("cfd.openQuantity")}</span>
            <div className={styles.marginInput}>
              <span
                className={styles.marginAdjustBtn}
                role="button"
                tabIndex={0}
                onClick={() => handleAdjustQuantity(-quantityAdjustStep)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleAdjustQuantity(-quantityAdjustStep)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="none">
                  <path d="M4 8H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <input
                type="text"
                placeholder={t("cfd.pleaseEnterQuantity")}
                value={quantityInputDisplayValue}
                onChange={handleQuantityInput}
              />
              <span
                className={styles.marginAdjustBtn}
                role="button"
                tabIndex={0}
                onClick={() => handleAdjustQuantity(quantityAdjustStep)}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleAdjustQuantity(quantityAdjustStep)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="none">
                  <path d="M8 4V12M4 8H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </div>
            <div className={styles.quickQuantityOptions}>
              {quickQuantityOptions.map((q) => (
                <button
                  key={q}
                  type="button"
                  className={styles.quickBtn}
                  onClick={() => handleQuantityShortcutAdd(q)}
                >
                  {q} {coinQuantityUpperCase}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 杠杆和开仓信息 */}
      <div className={styles.infoSection}>
        <div className={styles.leverageRow} data-guide-target="leverageRow">
          <span className={styles.label}>{t("cfd.leverage")}</span>
          <LeverageSelector
            value={leverage}
            min={cfdConfig.leverageRange?.min || 1}
            max={cfdConfig.leverageRange?.max || 200}
            onChange={setLeverage}
            width={136}
          />
        </div>
        <div className={styles.openSummaryRow}>
          <div className={styles.openSummaryItem}>
            <span className={styles.openSummaryLabel}>{t("cfd.openQuantity")}</span>
            <span className={styles.openSummaryValue}>
              {quantityDisplay} {coinQuantityUpperCase}
            </span>
          </div>
          <div className={cx(styles.openSummaryItem, styles.openSummaryItemRight)}>
            <span className={styles.openSummaryLabel}>{t("cfd.openValue")}</span>
            <span className={styles.openSummaryValue}>
              {value} {coinPriceUpperCase}
            </span>
          </div>
        </div>
        <div className={styles.stopLossRow}>
          <span className={styles.label}>{t("cfd.takeProfitStopLoss")}：{tpSlRangeText}</span>
        </div>
      </div>

      {/* 买涨/买跌按钮 */}
      <div className={styles.actionButtons} data-guide-target="actionButtons">
        {isLogin ? (
          <button
            type="button"
            className={cx(styles.actionBtn, activeSideBtnClass)}
            data-trade-side={tradeSide}
            onClick={() => handleSubmit(tradeSide)}
            disabled={loading}
          >
            {loading
              ? t("cfd.submitting")
              : tradeSide === "buy"
                ? t("cfd.standardActionBuyLong")
                : t("cfd.standardActionSellShort")}
          </button>
        ) : (
          <button
            type="button"
            className={cx(styles.actionBtn, styles.loginBtn)}
            data-trade-side="buy"
            onClick={() => {
              const query = "?backurl=" + encodeURIComponent(location.href);
              location.href = getUrl("/accounts/login" + query);
            }}
          >
            {t("trade.login2Register")}
          </button>
        )}
      </div>

      {/* 余额不足弹窗 */}
      <Modal
        className={styles.insufficientBalanceModal}
        open={insufficientBalanceModalOpen}
        onCancel={() => setInsufficientBalanceModalOpen(false)}
        footer={null}
        centered
        closable={false}
        width={320}
        maskClosable={true}
      >
        <div className={styles.insufficientBalanceContent}>
          <div className={styles.insufficientBalanceTitle}>{t("cfd.tip")}</div>
          <div className={styles.insufficientBalanceDesc}>{t("cfd.insufficientBalanceDesc")}</div>
          <div className={styles.insufficientBalanceActions}>
            <button className={styles.insufficientBalanceCancel} onClick={() => setInsufficientBalanceModalOpen(false)}>
              {t("cancel")}
            </button>
            <button className={styles.insufficientBalanceDeposit} onClick={handleInsufficientBalanceDeposit}>
              {t("cfd.deposit")}
            </button>
          </div>
        </div>
      </Modal>
      <DepositOrConvertDrawer
        open={choiceSheetOpen}
        onClose={() => setChoiceSheetOpen(false)}
        isFiatContract={isFiatContract}
        onChooseDeposit={handleChooseDeposit}
      />
    </div>
  );
};

export default observer(TradeForm);
